import React, { useState, useEffect, useCallback } from "react"
import {
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Container,
  Badge,
  ListGroup,
  ListGroupItem,
  Input,
  Table,
  Label,
  Form,
  Spinner,
} from "reactstrap"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import { toast, ToastContainer } from "react-toastify"
import logo from "../../assets/images/logo.png"
import "react-toastify/dist/ReactToastify.css"
import { useHistory } from "react-router-dom"
import ReactPaginate from "react-paginate"
import { URLS } from "../../Url"
import axios from "axios"

// Constants
const PRIORITY_BADGE_MAP = {
  high: "danger",
  medium: "warning",
  low: "success",
}

const PAYMENT_STATUS_BADGE_MAP = {
  completed: "success",
  pending: "warning",
  failed: "danger",
  active: "success",
  inactive: "secondary",
}

const ITEMS_PER_PAGE = 10

function ViewDeal() {
  const history = useHistory()
  const [deal, setDeal] = useState({})
  const [loading, setLoading] = useState(true)
  const Dealid = sessionStorage.getItem("Dealid")

  // Payment state
  const [advPayment, setAdvPayment] = useState({
    transactionId: "-",
    amount: "-",
    date: "-",
    time: "-",
    paymentStatus: "-",
  })

  // Products state
  const [showForm, setShowForm] = useState(false)
  const [editingProduct, setEditingProduct] = useState(null)
  const [dealProducts, setDealProducts] = useState([])
  const [products, setProducts] = useState([])
  const [searchTerm, setSearchTerm] = useState("")
  const [pageNumber, setPageNumber] = useState(0)

  // Form state
  const [form, setForm] = useState({
    price: "",
    productId: "",
  })

  // Auth token helper
  const getAuthToken = useCallback(() => {
    try {
      const authUser = localStorage.getItem("authUser")
      if (!authUser) return null
      const tokenJson = JSON.parse(authUser)
      return tokenJson?.token || null
    } catch (error) {
      console.error("Error parsing auth token:", error)
      return null
    }
  }, [])

  const token = getAuthToken()
  const authData = JSON.parse(localStorage.getItem("authUser") || "{}")
  const vendorDetails = authData?.vendor || {}

  // Format date helper
  const formatDate = useCallback(dateString => {
    if (!dateString) return "N/A"
    const date = new Date(dateString)
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    })
  }, [])

  // Fetch deal data
  const fetchDealData = useCallback(async () => {
    if (!token) {
      toast.error("Authentication token not found")
      return
    }

    try {
      setLoading(true)
      const response = await axios.post(
        URLS.GetOneDeal,
        { id: Dealid },
        { headers: { Authorization: `Bearer ${token}` } }
      )

      setDeal(response?.data?.deal || {})
      const paymentData = response?.data?.deal?.payments || {}
      setAdvPayment({
        transactionId: paymentData.transactionId || "-",
        amount: paymentData.amount || "-",
        date: paymentData.date || "-",
        time: paymentData.time || "-",
        paymentStatus: paymentData.paymentStatus || "-",
      })
    } catch (error) {
      console.error("Error fetching deal:", error)
      toast.error("Failed to fetch deal data")
    } finally {
      setLoading(false)
    }
  }, [Dealid, token])

  // Fetch deal products
  const fetchDealProducts = useCallback(async () => {
    try {
      const response = await axios.post(
        URLS.GetDealProduct,
        { dealId: Dealid },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      setDealProducts(response.data.dealproducts || [])
    } catch (error) {
      console.error("Error fetching deal products:", error)
      toast.error("Failed to load deal products")
    }
  }, [Dealid, token])

  // Fetch all products
  const fetchAllProducts = useCallback(async () => {
    try {
      const response = await axios.post(
        URLS.GetProduct,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      )
      setProducts(response.data.products || [])
    } catch (error) {
      console.error("Error fetching products:", error)
      toast.error("Failed to load products")
    }
  }, [token])

  // Search products
  const handleSearch = useCallback(
    async e => {
      const value = e.target.value
      setSearchTerm(value)

      if (value.trim() === "") {
        fetchDealProducts()
        return
      }

      try {
        const response = await axios.post(
          `${URLS.GetDealSearchProduct}${value}`,
          { dealId: Dealid },
          { headers: { Authorization: `Bearer ${token}` } }
        )
        setDealProducts(response.data.dealproducts || [])
        setPageNumber(0)
      } catch (error) {
        if (error.response?.status === 400) {
          toast.error(error.response.data.message)
        }
      }
    },
    [Dealid, fetchDealProducts, token]
  )

  // Form handlers
  const handleInputChange = e => {
    const { name, value } = e.target
    setForm(prev => ({ ...prev, [name]: value }))
  }

  const handleEditInputChange = e => {
    const { name, value } = e.target
    setEditingProduct(prev => ({ ...prev, [name]: value }))
  }

  // Product form management
  const openAddForm = () => {
    window.scrollTo({ top: 0, behavior: "smooth" })
    setShowForm(true)
    setEditingProduct(null)
    setForm({ price: "", productId: "" })
  }

  const openEditForm = product => {
    window.scrollTo({ top: 0, behavior: "smooth" })
    setEditingProduct(product)
    setShowForm(false)
  }

  const closeForms = () => {
    setShowForm(false)
    setEditingProduct(null)
  }

  // Add/Edit product submit handlers
  const handleAddSubmit = async e => {
    e.preventDefault()
    try {
      const response = await axios.post(
        URLS.AddDealProduct,
        {
          dealId: Dealid,
          price: form.price,
          productId: form.productId,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      )

      if (response.status === 200) {
        toast.success(response.data.message)
        closeForms()
        fetchDealProducts()
      }
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error(error.response.data.message)
      }
    }
  }

  const handleEditSubmit = async e => {
    e.preventDefault()
    try {
      const response = await axios.put(
        `${URLS.EditDealProduct}${editingProduct._id}`,
        {
          dealId: Dealid,
          price: editingProduct.price,
          productId: editingProduct.productId,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      )

      if (response.status === 200) {
        toast.success(response.data.message)
        closeForms()
        fetchDealProducts()
      }
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error(error.response.data.message)
      }
    }
  }

  // Delete product
  const deleteDealProduct = async product => {
    const confirmDelete = window.confirm(
      "Do you really want to delete this product from the deal?"
    )
    if (!confirmDelete) return

    try {
      const response = await axios.delete(
        `${URLS.DeleteDealProduct}${product._id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      )

      if (response.status === 200) {
        toast.success(response.data.message)
        fetchDealProducts()
      }
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error(error.response.data.message)
      }
    }
  }

  // Razorpay payment integration
  const handlePayment = () => {
    const totals = parseInt(deal.priorityPirce) * 100
    const options = {
      key: "rzp_test_HJG5Rtuy8Xh2NB",
      amount: totals,
      currency: "INR",
      name: "Bizzmate",
      description: "Bizzmate Transaction",
      image: logo,
      handler: async function (response) {
        try {
          const paymentResponse = await axios.post(
            URLS.AddDealPayment,
            {
              dealId: Dealid,
              paymentStatus: "completed",
              transactionId: response.razorpay_payment_id,
              amount: deal.priorityPirce,
              status: "active",
            },
            { headers: { Authorization: `Bearer ${token}` } }
          )

          if (paymentResponse.status === 200) {
            toast.success(paymentResponse.data.message)
            fetchDealData()
          }
        } catch (error) {
          console.error("Payment processing error:", error)
          toast.error("Failed to process payment")
        }
      },
      prefill: {
        name: `${vendorDetails.firstName || ""} ${
          vendorDetails.lastName || ""
        }`,
        email: vendorDetails.email || "",
        contact: vendorDetails.sellerMobile || "",
      },
      theme: {
        color: "#00578E",
      },
    }

    const rzp1 = new window.Razorpay(options)
    rzp1.open()
  }

  // Render helpers
  const renderPriorityBadge = priority => (
    <Badge
      color={PRIORITY_BADGE_MAP[priority?.toLowerCase()] || "secondary"}
      className="font-size-12"
      pill
    >
      {priority || "Not specified"}
    </Badge>
  )

  const renderPaymentStatusBadge = status => (
    <b
      className={`text-${
        PAYMENT_STATUS_BADGE_MAP[status?.toLowerCase()] || "primary"
      } text-capitalize`}
      // className="text-capitalize"
    >
      {status?.toLowerCase()}
    </b>
  )

  const renderListItems = (items, emptyMessage = "No items") => {
    if (!items || items.length === 0) return emptyMessage
    const itemArray = Array.isArray(items) ? items : items.split(",")
    return (
      <ListGroup flush className="border-0">
        {itemArray.map((item, index) => (
          <ListGroupItem key={index} className="px-0 py-1 border-0">
            <i className="mdi mdi-check-circle-outline text-success me-2"></i>
            {item.trim()}
          </ListGroupItem>
        ))}
      </ListGroup>
    )
  }

  // Pagination
  const pageCount = Math.ceil(dealProducts.length / ITEMS_PER_PAGE)
  const changePage = ({ selected }) => setPageNumber(selected)
  const displayedProducts = dealProducts
    .filter(product =>
      product.productName?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .slice(pageNumber * ITEMS_PER_PAGE, (pageNumber + 1) * ITEMS_PER_PAGE)

  // Initial data fetch
  useEffect(() => {
    fetchDealData()
    fetchDealProducts()
    fetchAllProducts()
  }, [fetchDealData, fetchDealProducts, fetchAllProducts])

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs
            title="Bizzmate Supplier"
            breadcrumbItem="Deal Management"
          />

          {/* Action Buttons */}

          {loading ? (
            <Card>
              <CardBody className="text-center py-5">
                <Spinner color="primary" />
                <p className="mt-2 mb-0">Loading deal details...</p>
              </CardBody>
            </Card>
          ) : (
            <>
              {/* Add Product Form */}
              {showForm && (
                <Card className="mb-2">
                  <CardBody>
                    <Form onSubmit={handleAddSubmit}>
                      <h5 className="mb-3">Add Product to Deal</h5>
                      <hr />
                      <Row>
                        <Col md="6">
                          <div className="mb-3">
                            <Label>
                              Product Name{" "}
                              <span className="text-danger">*</span>
                            </Label>
                            <select
                              value={form.productId}
                              name="productId"
                              required
                              onChange={handleInputChange}
                              className="form-select"
                            >
                              <option value="">Select Product</option>
                              {products.map(product => (
                                <option key={product._id} value={product._id}>
                                  {product.name}
                                </option>
                              ))}
                            </select>
                          </div>
                        </Col>
                        <Col md="6">
                          <Label>
                            Offer Price <span className="text-danger">*</span>
                          </Label>
                          <Input
                            onChange={handleInputChange}
                            name="price"
                            value={form.price}
                            required
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder="Enter Offer Price"
                          />
                        </Col>
                      </Row>
                      <div className="text-end mt-4">
                        <Button
                          type="button"
                          onClick={closeForms}
                          color="danger"
                          className="me-2 btn-sm"
                        >
                          Cancel <i className="bx bx-x-circle"></i>
                        </Button>
                        <Button
                          type="submit"
                          color="primary"
                          className=" btn-sm"
                        >
                          Submit <i className="bx bx-check-circle"></i>
                        </Button>
                      </div>
                    </Form>
                  </CardBody>
                </Card>
              )}

              {/* Edit Product Form */}
              {editingProduct && (
                <Card className="mb-2">
                  <CardBody>
                    <Form onSubmit={handleEditSubmit}>
                      <h5 className="mb-3">Edit Deal Product</h5>
                      <hr />
                      <Row>
                        <Col md="6">
                          <div className="mb-3">
                            <Label>
                              Product Name{" "}
                              <span className="text-danger">*</span>
                            </Label>
                            <select
                              value={editingProduct.productId}
                              name="productId"
                              required
                              onChange={handleEditInputChange}
                              className="form-select"
                            >
                              <option value="">Select Product</option>
                              {products.map(product => (
                                <option key={product._id} value={product._id}>
                                  {product.name}
                                </option>
                              ))}
                            </select>
                          </div>
                        </Col>
                        <Col md="6">
                          <Label>
                            Offer Price <span className="text-danger">*</span>
                          </Label>
                          <Input
                            required
                            name="price"
                            type="number"
                            min="0"
                            step="0.01"
                            value={editingProduct.price}
                            placeholder="Enter Offer Price"
                            onChange={handleEditInputChange}
                          />
                        </Col>
                      </Row>
                      <div className="text-end mt-4">
                        <Button
                          type="button"
                          onClick={closeForms}
                          color="danger"
                          className="btn-sm me-2"
                        >
                          Cancel <i className="bx bx-x-circle"></i>
                        </Button>
                        <Button type="submit" color="primary" className="btn-sm">
                          Update <i className="bx bx-check-circle"></i>
                        </Button>
                      </div>
                    </Form>
                  </CardBody>
                </Card>
              )}

              {/* Deal Information */}
              <Row>
                <Col md={12}>
                  <Card className="mb-2">
                    <CardBody>
                      <Row className="mb-3">
                        <Col className="text-end"></Col>
                      </Row>
                      <div className="d-flex align-items-center mb-4">
                        <div className="flex-grow-1">
                          <h4 className="card-title mb-1">
                            {deal.name || "Deal Details"} ({" "}
                            <b
                              className={`text-${
                                deal.status === "active" ? "success" : "danger"
                              }`}
                            >
                              {deal.status || "inactive"}
                            </b>
                            )
                          </h4>
                        </div>
                        <div>
                          {deal.status !== "active" && (
                            <Button
                              onClick={handlePayment}
                              color="primary"
                              className="me-2"
                              size="sm"
                            >
                              Add Payment
                            </Button>
                          )}
                          <Button
                            onClick={() => history.goBack()}
                            color="primary"
                            className="me-2"
                            size="sm"
                          >
                            <i className="fas fa-arrow-left me-2"></i> Back
                          </Button>
                        </div>
                      </div>

                      <Row>
                        {/* Basic Info Card */}
                        <Col md={6} lg={4}>
                          <h6 className="text-muted mb-3">Basic Information</h6>
                          <div className="mb-2">
                            <small className="text-muted">Category</small>
                            <p className="mb-0">
                              {deal.dealcategoryName || "N/A"}
                            </p>
                          </div>
                          <div className="mb-2">
                            <small className="text-muted">Priority</small>
                            <p className="mb-0">
                              {renderPriorityBadge(deal.priority)}
                            </p>
                          </div>
                          <div className="mb-2">
                            <small className="text-muted">Priority Price</small>
                            <p className="mb-0">
                              {deal.priorityPirce
                                ? `$${deal.priorityPirce}`
                                : "N/A"}
                            </p>
                          </div>
                          <div>
                            <small className="text-muted">Quantity</small>
                            <p className="mb-0">{deal.quantity || "N/A"}</p>
                          </div>
                        </Col>

                        {/* Date Info Card */}
                        <Col md={6} lg={4}>
                          <h6 className="text-muted mb-3">Date Information</h6>
                          <div className="mb-2">
                            <small className="text-muted">Start Date</small>
                            <p className="mb-0">{formatDate(deal.fromDate)}</p>
                          </div>
                          <div className="mb-2">
                            <small className="text-muted">End Date</small>
                            <p className="mb-0">{formatDate(deal.toDate)}</p>
                          </div>
                          <div>
                            <small className="text-muted">Apply For</small>
                            <p className="mb-0">{deal.applyFor || "N/A"}</p>
                          </div>
                        </Col>

                        {/* Coverage Card */}
                        <Col md={6} lg={4}>
                          <h6 className="text-muted mb-3">Coverage</h6>
                          <div className="mb-3">
                            <small className="text-muted">{deal.type} :</small>
                            {renderListItems(
                              deal.deliveryIdsNames,
                              "No cities specified"
                            )}
                          </div>
                          <div>
                            <small className="text-muted">Customers</small>
                            {renderListItems(
                              deal.customerNames,
                              "No customers specified"
                            )}
                          </div>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                </Col>

                {/* Payment Information */}
                <Col md={12}>
                  <Card>
                    <CardBody>
                      <h5 className="mb-4">
                        <i className="fas fa-receipt me-2"></i>
                        Payment Information
                      </h5>
                      <ListGroup flush>
                        <ListGroupItem className="d-flex justify-content-between align-items-center py-3">
                          <span className="fw-semibold">
                            <i className="fas fa-id-card me-2"></i>
                            Transaction ID:
                          </span>
                          <span className="text-muted">
                            {advPayment.transactionId}
                          </span>
                        </ListGroupItem>
                        <ListGroupItem className="d-flex justify-content-between align-items-center py-3">
                          <span className="fw-semibold">
                            <i className="fas fa-rupee-sign me-2"></i>
                            Amount:
                          </span>
                          <span className="text-muted">
                            ₹{advPayment.amount}
                          </span>
                        </ListGroupItem>
                        <ListGroupItem className="d-flex justify-content-between align-items-center py-3">
                          <span className="fw-semibold">
                            <i className="far fa-calendar-alt me-2"></i>
                            Date:
                          </span>
                          <span className="text-muted">{advPayment.date}</span>
                        </ListGroupItem>
                        <ListGroupItem className="d-flex justify-content-between align-items-center py-3">
                          <span className="fw-semibold">
                            <i className="far fa-clock me-2"></i>
                            Time:
                          </span>
                          <span className="text-muted">{advPayment.time}</span>
                        </ListGroupItem>
                        <ListGroupItem className="d-flex justify-content-between align-items-center py-3">
                          <span className="fw-semibold">
                            <i className="fas fa-info-circle me-2"></i>
                            Status:
                          </span>
                          {renderPaymentStatusBadge(advPayment.paymentStatus)}
                        </ListGroupItem>
                      </ListGroup>
                    </CardBody>
                  </Card>
                </Col>

                {/* Deal Products */}
                <Col md={12}>
                  <Card>
                    <CardBody>
                      <Row className="mb-3">
                        <Col md={6}>
                          <Button
                            color="primary"
                            onClick={openAddForm}
                            className="btn-sm"
                          >
                            <i className="bx bx-plus me-2"></i>
                            Add Product to Deal
                          </Button>
                        </Col>
                        <Col md={6}>
                          <div className="float-end">
                            <Input
                              type="search"
                              placeholder="Search products..."
                              value={searchTerm}
                              onChange={handleSearch}
                              style={{ maxWidth: "300px" }}
                            />
                          </div>
                        </Col>
                      </Row>

                      <div className="table-responsive">
                        <Table hover bordered>
                          <thead>
                            <tr className="text-center">
                              <th>#</th>
                              <th>Product Name</th>
                              <th>Regular Price</th>
                              <th>Deal Price</th>
                              <th>Status</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {displayedProducts.length > 0 ? (
                              displayedProducts.map((product, index) => (
                                <tr key={product._id} className="text-center">
                                  <td>
                                    {pageNumber * ITEMS_PER_PAGE + index + 1}
                                  </td>
                                  <td>{product.productName}</td>
                                  <td>${product.productActualPrice}</td>
                                  <td>${product.price}</td>
                                  <td>
                                    <b
                                      // color={
                                      //   product.status === "active"
                                      //     ? "success"
                                      //     : "danger"
                                      // }
                                      className={`text-${
                                        product.status === "active"
                                          ? "success"
                                          : "danger"
                                      }`}
                                    >
                                      {product.status}
                                    </b>
                                  </td>
                                  <td>
                                    <div className="d-flex justify-content-center">
                                      {/* <Button
                                        color="primary"
                                        size="sm"
                                        className="me-2"
                                        onClick={() => openEditForm(product)}
                                        outline
                                      > */}
                                      <i
                                        className="bx bx-edit text-primary me-2 "
                                        onClick={() => openEditForm(product)}
                                        title="Edit"
                                        style={{
                                          fontSize: "22px",
                                          cursor: "pointer",
                                        }}
                                      ></i>
                                      {/* </Button>
                                      <Button
                                        color="primary"
                                        size="sm"
                                        onClick={() =>
                                          deleteDealProduct(product)
                                        }
                                        outline
                                      > */}
                                      <i
                                        className="bx bx-trash text-primary"
                                        title="Delete"
                                        onClick={() =>
                                          deleteDealProduct(product)
                                        }
                                        style={{
                                          fontSize: "22px",
                                          cursor: "pointer",
                                        }}
                                      ></i>
                                      {/* </Button> */}
                                    </div>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="6" className="text-center py-4">
                                  No products found for this deal
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </Table>
                      </div>

                      {dealProducts.length > ITEMS_PER_PAGE && (
                        <div className="d-flex justify-content-end mt-3">
                          <ReactPaginate
                            previousLabel={"Previous"}
                            nextLabel={"Next"}
                            pageCount={pageCount}
                            onPageChange={changePage}
                            containerClassName={"pagination"}
                            previousLinkClassName={"page-link"}
                            nextLinkClassName={"page-link"}
                            disabledClassName={"disabled"}
                            activeClassName={"active"}
                            pageClassName={"page-item"}
                            pageLinkClassName={"page-link"}
                          />
                        </div>
                      )}
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </>
          )}
        </Container>
        <ToastContainer position="top-right" autoClose={3000} />
      </div>
    </React.Fragment>
  )
}

export default ViewDeal
